import type { AuthUser, Role } from '../types/domain';

/**
 * Decode the current user from the access-token JWT. The backend issues no /me
 * endpoint — identity lives entirely in the token claims (sub, email, name,
 * role[]). This is display/routing only; every real authorization decision is
 * re-enforced server-side (gateway route-role map + [RequireRoles]).
 */
interface JwtClaims {
  sub?: string;
  email?: string;
  name?: string;
  // ASP.NET emits role claims under this URI (possibly repeated → string | string[]).
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string | string[];
  role?: string | string[];
  tenant_id?: string;
  is_platform_admin?: string;
  exp?: number;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  if (typeof atob === 'function') return decodeURIComponent(escape(atob(padded)));
  // Node/SSR fallback
  return Buffer.from(padded, 'base64').toString('utf-8');
}

export function decodeUser(accessToken: string): AuthUser | null {
  try {
    const [, payload] = accessToken.split('.');
    if (!payload) return null;
    const claims = JSON.parse(base64UrlDecode(payload)) as JwtClaims;

    const rawRoles =
      claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? claims.role ?? [];
    // 'platform_admin' included alongside the 4 built-ins — was silently dropped here before,
    // which meant useAuthStore's user.roles never actually contained it even though the JWT
    // carried it correctly, live-caught while wiring the Phase 2 platform-admin UI.
    const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
      .map((r) => r.toLowerCase())
      .filter((r): r is Role => ['admin', 'manager', 'worker', 'customer', 'platform_admin'].includes(r));

    if (!claims.sub) return null;
    return {
      id: claims.sub,
      email: claims.email ?? '',
      name: claims.name ?? claims.email ?? '',
      roles,
      isPlatformAdmin: claims.is_platform_admin === 'true',
    };
  } catch {
    return null;
  }
}
